import {
  budgets,
  type EngineCommand,
  type EngineMessage,
  type EngineInfo,
  type EngineResult,
  type Search,
  type EngineLifecycle,
} from '../../domain/engine/protocol';
export interface EnginePort {
  postMessage(message: EngineCommand): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<EngineMessage>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
}
export class StockfishService {
  lifecycle: EngineLifecycle = 'unloaded';
  private worker?: EnginePort;
  private startup?: Promise<void>;
  private resolveReady?: () => void;
  private rejectReady?: (e: Error) => void;
  private failureCount = 0;
  private epoch = 0;
  private disposed = false;
  private request?: {
    search: Search;
    infos: EngineInfo[];
    resolve: (r: EngineResult) => void;
    reject: (e: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
  };
  constructor(
    private factory: () => EnginePort = () =>
      new Worker(new URL('../../workers/stockfish.worker.ts', import.meta.url), { type: 'module' }),
    private startupMs = budgets.startup,
  ) {}
  private async initialize(): Promise<void> {
    if (this.disposed || this.lifecycle === 'disabled') throw new Error('Engine disabled');
    if (this.worker && ['ready', 'thinking', 'stopping'].includes(this.lifecycle)) return;
    if (this.startup) return this.startup;
    this.startup = (async () => {
      while (this.failureCount < 2) {
        try {
          await this.spawn();
          return;
        } catch (e) {
          if (this.disposed) throw new DOMException('Cancelled', 'AbortError');
          this.failureCount++;
          this.worker?.terminate();
          this.worker = undefined;
          if (this.failureCount >= 2) {
            this.lifecycle = 'disabled';
            throw e;
          }
        }
      }
    })();
    try {
      await this.startup;
    } finally {
      this.startup = undefined;
    }
  }
  private spawn() {
    return new Promise<void>((resolve, reject) => {
      this.lifecycle = 'loading';
      const worker = this.factory();
      this.worker = worker;
      let settled = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.resolveReady = undefined;
        this.rejectReady = undefined;
        this.lifecycle = error ? 'failed' : 'ready';
        if (error) reject(error);
        else resolve();
      };
      const timer = setTimeout(
        () => finish(new Error('Stockfish startup timeout')),
        this.startupMs,
      );
      this.resolveReady = () => finish();
      this.rejectReady = finish;
      worker.onmessage = (event) => {
        if (this.worker !== worker || this.disposed) return;
        this.receive(event.data);
      };
      worker.onerror = (event) => {
        if (this.worker !== worker) return;
        this.failure(new Error(event.message || 'Engine worker failed'));
      };
      this.lifecycle = 'waiting-uciok';
      try {
        worker.postMessage({
          type: 'init',
          generation: 0,
          hash: typeof navigator !== 'undefined' && navigator.hardwareConcurrency <= 4 ? 16 : 32,
        });
      } catch (error) {
        finish(this.transportError(error));
      }
    });
  }
  private receive(message: EngineMessage) {
    if (message.type === 'ready') {
      this.resolveReady?.();
      this.resolveReady = undefined;
      this.rejectReady = undefined;
      return;
    }
    if (message.type === 'error') {
      this.failure(new Error(message.message));
      return;
    }
    const r = this.request;
    if (
      !r ||
      message.generation !== r.search.generation ||
      message.requestId !== r.search.requestId
    )
      return;
    if (message.type === 'info') {
      r.infos.push(message);
      if (r.infos.length > 64) r.infos.shift();
    }
    if (message.type === 'best-move') {
      clearTimeout(r.timeout);
      this.request = undefined;
      this.lifecycle = 'ready';
      r.resolve({
        generation: message.generation,
        requestId: message.requestId,
        move: message.move,
        infos: r.infos,
      });
    }
  }
  private failure(error: Error) {
    if (this.disposed || this.lifecycle === 'failed' || this.lifecycle === 'disabled') return;
    if (this.rejectReady) {
      this.rejectReady(error);
      return;
    }
    this.failureCount++;
    this.lifecycle = this.failureCount >= 2 ? 'disabled' : 'failed';
    if (this.request) {
      clearTimeout(this.request.timeout);
      this.request.reject(error);
      this.request = undefined;
    }
    this.worker?.terminate();
    this.worker = undefined;
  }
  async search(search: Search): Promise<EngineResult> {
    const epoch = ++this.epoch;
    for (;;) {
      if (epoch !== this.epoch || this.disposed) throw new DOMException('Cancelled', 'AbortError');
      await this.initialize();
      if (epoch !== this.epoch || this.disposed) throw new DOMException('Cancelled', 'AbortError');
      this.cancelActive();
      this.lifecycle = 'thinking';
      try {
        return await new Promise<EngineResult>((resolve, reject) => {
          const timeout = setTimeout(
            () => this.failure(new Error('Engine search timeout')),
            search.movetime + 3000,
          );
          this.request = { search, infos: [], resolve, reject, timeout };
          try {
            this.worker!.postMessage({ type: 'analyze', ...search });
          } catch (error) {
            this.failure(this.transportError(error));
          }
        });
      } catch (error) {
        // One lifetime restart, shared with startup failures. Never retry cancelled work.
        if (this.worker || this.failureCount >= 2 || epoch !== this.epoch || this.disposed)
          throw error;
      }
    }
  }
  private cancelActive() {
    if (this.request) {
      const r = this.request;
      this.request = undefined;
      clearTimeout(r.timeout);
      try {
        this.worker?.postMessage({
          type: 'stop',
          generation: r.search.generation,
          requestId: r.search.requestId,
        });
      } catch (error) {
        this.failure(this.transportError(error));
      } finally {
        r.reject(new DOMException('Cancelled', 'AbortError'));
      }
    }
  }
  stop() {
    this.epoch++;
    this.cancelActive();
    if (this.lifecycle === 'thinking') this.lifecycle = 'ready';
  }
  dispose() {
    if (this.disposed) return;
    this.stop();
    this.disposed = true;
    try {
      this.worker?.postMessage({ type: 'dispose', generation: 0 });
    } catch {
      /* Termination below also releases a worker with a broken message port. */
    }
    this.worker?.terminate();
    this.worker = undefined;
    this.rejectReady?.(new Error('Disposed'));
    this.lifecycle = 'disabled';
  }
  private transportError(error: unknown) {
    return error instanceof Error ? error : new Error('Engine message transport failed');
  }
}
